import {
  AfterViewInit,
  ChangeDetectorRef,
  computed,
  DestroyRef,
  Directive,
  effect,
  EffectRef,
  ElementRef,
  HostBinding,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ErrorStateMatcher } from '@angular/material/core';
import { ErrorsService } from '@partners/errors';
import { IInputMask, IMaskOptions, INumericInputMask, isArray, isBoolean, isEmpty, isFunction } from '@partners/utils';
import { isObservable, Observable, of, pairwise, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap } from 'rxjs/operators';
import { FeatureFormComponent } from '../../components';
import { ExtendedFormControl } from '../../controls/control';
import { composeAsyncValidators, composeValidators, shouldUpdateControlState } from '../../functions';
import {
  IFieldValidation,
  IFormField,
  IValidatorState,
  TControlChangeTrigger,
  TFieldBeforeValueChange,
  TFieldDestroy,
  TFieldHint,
  TFieldInitialize,
  TFieldInputMode,
  TFieldLocals,
  TFieldReset,
  TFieldSelect,
  TFieldStateArgs,
  TFieldType,
  TFieldValueChange,
  TFormElement,
  TIterableOption
} from '../../model';
import { preferThrottleInterval } from '../../operators';

@Directive({
  standalone: true,
  selector: '[simpleField]',
  exportAs: 'field'
})
export class SimpleFieldDirective<T = unknown> implements OnInit, OnDestroy, AfterViewInit {
  @Input()
  simpleField: string;

  @HostBinding('class.bidi')
  bidiClass: boolean;

  fieldConfig: IFormField<T>;

  isReady$$ = signal<boolean>(false);

  // #region members
  name?: string;
  value?: T;
  path?: string;
  label?: string;
  minorLabel: string;
  mask?: IMaskOptions;
  masking?: IInputMask | INumericInputMask;
  hint?: TFieldHint;
  validation?: IFieldValidation;
  disabled?: boolean;
  hidden?: boolean;
  errorMatcher?: ErrorStateMatcher;
  elementType?: TFormElement;
  inputType?: TFieldType;
  inputMode?: TFieldInputMode;
  extraData?: { [key: string]: any };
  debounceInterval?: number;
  throttleInterval?: number;
  changeTrigger?: TControlChangeTrigger;
  keyProp: string;
  valueProp: string;
  maxOptions?: number;
  pattern?: string;
  isPending$$ = signal<boolean>(false);
  options$$? = signal<TIterableOption[]>([]);

  onReset: TFieldReset<T>;
  onInit: TFieldInitialize<T>;
  onSelect: TFieldSelect<T>;
  onDestroy: TFieldDestroy;
  valueChange: TFieldValueChange<T>;
  beforeChange: TFieldBeforeValueChange<T>;

  // #endregion

  get control(): ExtendedFormControl<T> {
    return this.fieldConfig?.control;
  }
  get nativeElement(): HTMLElement {
    return this._elementRef.nativeElement;
  }

  /**
   * TODO: a temporary solution:
   * Form controls without mat-form-field (checkbox | custom controls etc'), do not display errors synchronously
   * (i.e. required on submit), so we use this to reflect their current valid state to the view
   */
  get hasErrors$(): Observable<boolean> {
    if (!this.control) {
      return of(false);
    }
    if (this.errorMatcher) {
      return of(this.errorMatcher?.isErrorState(this.control, this._featureForm.formHandler));
    }
    return of(this.control.touched && this.control.invalid);
  }

  submitted$$ = computed<boolean>(() => this._featureForm.submitted$$());

  private _valueChangeSubscription: Subscription;

  private _subscription: Subscription;
  private _stateEffectRef: EffectRef;
  private _focusEventRef: () => void;
  private _blurEventRef: () => void;

  private _localInjector = inject(Injector);
  private _errorsService = inject(ErrorsService);
  private _featureForm = inject(FeatureFormComponent);
  private _elementRef = inject(ElementRef);
  private _renderer = inject(Renderer2);
  private _destroyRef = inject(DestroyRef);
  private _changeDetector = inject(ChangeDetectorRef);

  constructor() {
    this._subscription = new Subscription();
  }

  ngAfterViewInit(): void {
    // this.applySelectOnFocus();
    // this.applyBlurOnFocusOut();
  }

  ngOnInit(): void {
    /** extract all props values from the field's config object and apply them to the field's ui */
    this.initializeField(this.simpleField);
    this.isReady$$.set(true);
  }

  ngOnDestroy(): void {
    this._focusEventRef && this._focusEventRef();
    this._stateEffectRef && this._stateEffectRef.destroy();
    this._subscription && this._subscription.unsubscribe();
  }

  getFieldLocals(): TFieldLocals<T> {
    return { ...this._featureForm.locals, field: this.fieldConfig };
  }

  /**
   * @description
   * in rare cases we would need to declare value change to detect changes in the model, but we would
   * have a private change handler, so we don't want to subscribe to the default one
   */
  cancelValueChangeHandler() {
    this._valueChangeSubscription && this._valueChangeSubscription.unsubscribe();
  }

  parseOptionValue({ source, option }: MatAutocompleteSelectedEvent) {
    const iterableOption = option.value;
    this.control.setValue(iterableOption[this.valueProp]);
  }

  protected initializeField(fieldName: string) {
    /** locate the field's config class in the parent form's fields collection */
    this.fieldConfig = this._featureForm.findFieldConfig(fieldName);

    if (!this.fieldConfig) {
      this._errorsService.throwLocalError({ message: `Failed to initialize "${fieldName}" field handler` });
    }

    /** add an effect to capture and respond to field's state updates */
    this.addStateListener();

    /** extract and apply the values of the state to the fields properties & ui elements */
    this.applyFieldConfig(this.fieldConfig);
  }

  /**
   * @description
   * attach a handler to a "focus" event on an input element, to allow selection (of its content)
   * on focus as a default behavior
   */
  private applySelectOnFocus(): void {
    if (!isFunction(this.nativeElement.querySelector)) {
      return;
    }
    const input = this.nativeElement.querySelector('input');
    if (!input) {
      return;
    }
    /** apply select-on-focus behavior for the form control's input element */
    this._focusEventRef = this._renderer.listen(input, 'focus', () => {
      input.select();
    });
  }

  /**
   * @description
   * attach a handler to a "focusout" event on an input element.
   * this comes to answer a refocus happening twice when clicking outside app, triggering
   * mask on the focused-out empty input and focusing on another input
   * Currently not used and marked at ngAfterViewInit as this is unlikely to happen to a user
   */
  private applyBlurOnFocusOut(): void {
    if (!isFunction(this.nativeElement.querySelector)) {
      return;
    }
    const input = this.nativeElement.querySelector('input');
    if (!input) {
      return;
    }
    /** apply focus out behavior for the form control's input element */
    this._blurEventRef = this._renderer.listen(input, 'focusout', () => {
      input.blur();
    });
  }

  /**
   * @description
   * Track changes made by the state emitter (signal). This effect will trigger every time one of the state args
   * of the field changes: (1) The options[] list of a dropdown (2) The disable state (3) The control's value.. etc'
   * (see TFieldStateArgs for all)
   */
  private addStateListener() {
    this._stateEffectRef = effect(
      () => {
        const isReady = this.isReady$$();
        if (!isReady) {
          return;
        }
        /** get a state object from the field with a snapshot of the "fieldConfig" latest values **/
        const state = this.fieldConfig.state$$();
        if (!state) {
          return;
        }

        /** extract latest state values */
        const { disabled, options, value, label, hidden, validation, extraData, isPending, pristine } = state;

        /** apply state changes to the view */
        shouldUpdateControlState('extraData', state) && this.updateExtraDataState(extraData);
        shouldUpdateControlState('label', state) && this.updateLabelState(label);
        shouldUpdateControlState('options', state) && this.updateOptionsState(options);
        shouldUpdateControlState('disabled', state) && this.updateDisabledState(disabled);
        shouldUpdateControlState('validation', state) && this.updateValidationState(validation);
        shouldUpdateControlState('hidden', state) && this.updateHiddenState(hidden);
        shouldUpdateControlState('value', state) && this.updateValue(value);
        shouldUpdateControlState('isPending', state) && this.updatePending(isPending);
        shouldUpdateControlState('pristine', state) && this.updatePristineState(pristine);

        this._changeDetector.markForCheck();
      },
      /** provide injector to satisfy requirement for consuming signal's effect outside injection context */
      { injector: this._localInjector, allowSignalWrites: true }
    );
  }
  updatePristineState(pristine: boolean) {
    if (pristine) {
      this.control.markAsPristine();
      this.control.markAsUntouched();
      this.control.updateValueAndValidity();
    }
  }

  updatePending(value: boolean) {
    this.isPending$$.set(value);
    // this._changeDetector.markForCheck();
  }

  private updateExtraDataState(extraData: { [key: string]: any }) {
    if (isEmpty(extraData)) {
      return;
    }

    this.fieldConfig.extraData = extraData;
  }

  private updateValidationState(validationStates: IValidatorState[] | boolean | IFieldValidation) {
    if (isEmpty(validationStates)) {
      return;
    }

    const { validationHandlers } = this.validation;

    /** cherry pick and edit specific validators' state (disabled | enabled) */
    if (isArray(validationStates)) {
      this.control.setValidators(composeValidators(validationHandlers, validationStates));
      this.control.setAsyncValidators(composeAsyncValidators(validationHandlers, validationStates));
    } else if (validationStates === false) {
      /** remove all validators */
      this.control.clearValidators();
      this.control.clearAsyncValidators();
    } else if (validationStates === true) {
      /** restore all validators */
      this.control.setValidators(composeValidators(validationHandlers));
      this.control.setAsyncValidators(composeAsyncValidators(validationHandlers));
    } else if (validationStates?.validationHandlers?.length) {
      this.validation.validationHandlers = validationStates?.validationHandlers;
      this.control.setValidators(composeValidators(validationStates?.validationHandlers));
      this.control.setAsyncValidators(composeAsyncValidators(validationStates?.validationHandlers));
    }
    this.control.updateValueAndValidity({ emitEvent: true, onlySelf: false });
  }

  private updateLabelState(value: string) {
    if (isEmpty(value)) {
      return;
    }
    this.label = value;
  }

  private updateValue(value: T) {
    this.control.setValue(value as T);
  }

  private updateOptionsState(options: TIterableOption[]): void {
    if (!isArray(options)) {
      return;
    }
    this.options$$.set(options);
  }

  private updateDisabledState(isDisabled: boolean): void {
    if (!isBoolean(isDisabled)) {
      return;
    }
    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }
  }

  private updateHiddenState(isHidden: boolean): void {
    if (!isBoolean(isHidden)) {
      return;
    }
    this.hidden = isHidden;
  }

  private applyFieldConfig(fieldConfig: IFormField<T>): void {
    const {
      name,
      path,
      value,
      label,
      minorLabel,
      mask,
      masking,
      hint,
      inputType,
      inputMode,
      validation,
      bidi,
      errorMatcher,
      debounceInterval,
      throttleInterval,
      keyProp,
      valueProp,
      disabled,
      changeTrigger,
      maxOptions,
      isPending,
      pattern,
      onInit,
      onReset,
      onSelect,
      onDestroy,
      valueChange,
      beforeChange
    } = fieldConfig;

    this.name = name;
    this.path = path;
    this.value = value;
    this.label = label;
    this.minorLabel = minorLabel;
    this.mask = mask;
    this.masking = masking;
    this.hint = hint;
    this.inputType = inputType;
    this.inputMode = inputMode;
    this.validation = validation;
    this.debounceInterval = debounceInterval;
    this.throttleInterval = throttleInterval;
    this.errorMatcher = errorMatcher;
    this.bidiClass = bidi;
    this.keyProp = keyProp;
    this.valueProp = valueProp;
    this.disabled = disabled;
    this.changeTrigger = changeTrigger;
    this.maxOptions = maxOptions;
    this.pattern = pattern;

    this.onInit = onInit;
    this.onReset = onReset;
    this.onSelect = onSelect;
    this.onDestroy = onDestroy;
    this.beforeChange = beforeChange;

    this.isPending$$.set(isPending);

    if (this.onInit) {
      /** bind & trigger an init event callback - this happens only once per field on ng's OnInit hook */
      this.addInitListener(onInit);
    }

    if (valueChange) {
      /** bind a change event to the form control's valueChanges stream       */
      this.addValueChangeListener(valueChange);
    }
  }

  private addInitListener(initCallback: TFieldInitialize<T>) {
    const callback = initCallback.bind(this.fieldConfig);
    callback(this.getFieldLocals());
  }

  /**
   * @description
   * Subscribe to the value changes of the form control, on each tick of the control's value stream -
   * trigger a callback function which was passed in to handle the change by consumer.
   *
   * @param changesCallback a callback function to call when the form control's value changes
   */
  addValueChangeListener(changesCallback: TFieldValueChange<T>) {
    /**
     * allow this field class to be referenced in the callback scope as "this" NOTE: this behavior is NOT
     * applicable in arrow function syntax (in arrow function "this" always refer to the context of the
     * class where the callback is declared)
     * */
    const changeCallback = changesCallback.bind(this.fieldConfig);

    /**
     * we can also attach a "before change callback" function which will execute just before the
     * main valueChange callback. this can be useful if we want to manipulate the values been emitted
     * from the main change event before it triggers, for example we can use it to convert visualized
     * values such as stringified number (1,000) to model numeric values (1000)
     * */
    let beforeChangeCallback: Function;
    if (isFunction(this.beforeChange)) {
      beforeChangeCallback = this.beforeChange.bind(this.fieldConfig);
    }

    this._valueChangeSubscription = this.control.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        distinctUntilChanged((prevValue, nextValue) => {
          return prevValue === nextValue;
        }),
        /**
         * seeds the pairwise with initial default value, in order to make it trigger on first value change
         * (and not after 2 changes as pairwise default behavior)
         **/
        startWith(undefined),
        /** pick up the previous value and pair with current value */
        pairwise(),
        /** avoid latency in case changes requires costly computing time or to numerous */
        preferThrottleInterval(this.throttleInterval, this.debounceInterval),
        map(([prevValue, nextValue]) => ({ prevValue, nextValue })),
        /** apply the custom callback function */
        filter(() => {
          if (this.changeTrigger === 'valid-only') {
            return this.control.valid;
          }
          return this.changeTrigger === 'always';
        }),
        switchMap((valuePair) => {
          // if (beforeChangeCallback) {
          //   valuePair = beforeChangeCallback(valuePair);
          // }

          // console.log(`"${this.name}" value change callback(${valuePair.nextValue})`);
          const result = changeCallback({ ...valuePair }, this.getFieldLocals());

          if (isObservable(result)) {
            return result as Observable<TFieldStateArgs<T>>;
          }

          return of(result);
        })
      )
      .subscribe();
  }
}
